import type { CSSProperties } from 'react'

import Image from 'next/image'
import { useRef, useState } from 'react'
import {
    ArrowDownLeft,
    ArrowUpRight,
    PiggyBank,
    ShoppingBasket,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/format'
import { LANDSCAPES, PORTRAIT_GRADE } from '@/lib/visual'
import { useInView } from '@/hooks/use-in-view'
import { CountUp } from '@/components/generals/count-up'
import { Landscape } from '@/components/generals/landscape'
import { Reveal } from '@/components/generals/reveal'
import { SectionHeading } from '@/components/generals/section-heading'

const BARS = [
    { label: 'Earned', amount: 9840, share: 86, note: 'of your monthly goal' },
    { label: 'Invested', amount: 2950, share: 62, note: 'of the yearly plan' },
    { label: 'Spent', amount: 4380, share: 48, note: 'of your budget used' }
]

const TRANSACTIONS = [
    { label: 'Invoice paid', detail: 'Harbor Design Co.', amount: 420, icon: ArrowDownLeft },
    { label: 'Weekly shop', detail: 'Corner Market', amount: -85.8, icon: ShoppingBasket },
    { label: 'Moved to savings', detail: 'Emergency fund', amount: -150, icon: PiggyBank }
]

const MonthlyOverviewCard = () => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { threshold: 0.35 })

    return (
        <div ref={ref} className='grain relative h-full min-h-[540px] overflow-hidden rounded-[32px] bg-ink'>
            <Landscape src={LANDSCAPES.wheatHaze} fade='soft' sizes='(min-width: 1024px) 50vw, 100vw' />
            <div aria-hidden='true' className='hairline pointer-events-none absolute inset-0 rounded-[inherit]' />

            <div className='relative z-10 flex h-full flex-col p-6 sm:p-9'>
                <div className='flex items-start justify-between gap-4'>
                    <div>
                        <p className='text-[15px] font-medium text-cream'>
                            Month in review
                        </p>

                        <p className='mt-1 text-xs text-cream/50'>
                            Every account, combined
                        </p>
                    </div>

                    <span className='glass inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs text-cream'>
                        <ArrowUpRight aria-hidden='true' className='size-3.5 text-citron' />
                        12.4% above last month
                    </span>
                </div>

                <div className='mt-auto pt-16'>
                    <p className='text-[11px] font-medium uppercase tracking-[0.3em] text-cream/50'>
                        Combined balance
                    </p>

                    <p className='mt-3 text-[2.75rem] font-normal leading-none tracking-[-0.045em] text-cream sm:text-6xl'>
                        <CountUp value={14250} decimals={2} format='currency' active={inView} duration={2000} />
                    </p>

                    <div className='glass mt-8 space-y-5 rounded-[22px] p-5 sm:p-6'>
                        {BARS.map((bar, index) => (
                            <div key={bar.label}>
                                <div className='flex items-baseline justify-between gap-4 text-sm'>
                                    <span className='text-cream/80'>
                                        {bar.label}
                                    </span>

                                    <span className='text-cream'>
                                        <CountUp value={bar.amount} format='currency' active={inView} delay={250 + index * 220} />
                                    </span>
                                </div>

                                <div className='mt-2.5 h-1.5 overflow-hidden rounded-full bg-white/10'>
                                    <div
                                        className='h-full rounded-full bg-[linear-gradient(90deg,#e2f37a,#8fae62)] transition-[width] duration-[1800ms] ease-cinema'
                                        style={{ width: inView ? `${bar.share}%` : '0%', transitionDelay: `${250 + index * 220}ms` } as CSSProperties}
                                    />
                                </div>

                                <p className='mt-1.5 text-[11px] text-cream/45'>
                                    <CountUp value={bar.share} suffix='%' active={inView} delay={250 + index * 220} /> {bar.note}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

const DailySnapshotCard = () => {
    const [open, setOpen] = useState(false)
    const net = TRANSACTIONS.reduce((sum, item) => sum + item.amount, 0)

    return (
        <div className='grain group relative h-full min-h-[540px] overflow-hidden rounded-[32px] bg-ink'>
            <div className='absolute inset-0 isolate'>
                <Image
                    src='/images/portraits/analytics-man.jpg'
                    alt='A Verdane member smiling on a hillside walk'
                    fill
                    sizes='(min-width: 1024px) 50vw, 100vw'
                    className={cn('object-cover transition-transform duration-[1600ms] ease-cinema group-hover:scale-[1.03]', PORTRAIT_GRADE)}
                    style={{ objectPosition: 'center 18%' }}
                />
                <div aria-hidden='true' className='absolute inset-0 bg-[linear-gradient(180deg,#c9d483_0%,#5d7b3a_100%)] opacity-30 mix-blend-color' />
                <div aria-hidden='true' className='absolute inset-0 bg-[radial-gradient(100%_60%_at_50%_0%,#f4d98e,transparent_70%)] opacity-50 mix-blend-soft-light' />
                <div aria-hidden='true' className='absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.35)_0%,rgba(0,0,0,0)_30%,rgba(0,0,0,0.1)_55%,rgba(0,0,0,0.85)_100%)]' />
            </div>
            <div aria-hidden='true' className='hairline pointer-events-none absolute inset-0 rounded-[inherit]' />

            <div className='absolute left-5 top-5 z-10 flex items-center gap-3 sm:left-7 sm:top-7'>
                <span className='glass rounded-full px-3 py-1.5 text-xs text-cream'>
                    Your day so far
                </span>
            </div>

            <div className='absolute inset-x-4 bottom-5 z-10 flex flex-col items-center sm:bottom-8'>
                <div
                    id='daily-transactions'
                    className={cn(
                        'glass-dark mb-3 w-full max-w-[380px] origin-bottom rounded-[22px] p-4 transition-all duration-700 ease-cinema',
                        open ? 'translate-y-0 scale-100 opacity-100' : 'pointer-events-none translate-y-4 scale-95 opacity-0'
                    )}
                    aria-hidden={!open}
                    inert={!open}
                >
                    <div className='flex items-center justify-between'>
                        <p className='text-xs font-medium uppercase tracking-[0.24em] text-cream/55'>
                            So far today
                        </p>

                        <button
                            type='button'
                            onClick={() => setOpen(false)}
                            aria-label='Close the activity list'
                            className='grid size-7 place-items-center rounded-full text-cream/60 hover:bg-white/10 hover:text-cream'
                        >
                            <X className='size-3.5' />
                        </button>
                    </div>

                    <ul className='mt-2 divide-y divide-white/[0.07]'>
                        {TRANSACTIONS.map(item => (
                            <li key={item.label} className='flex items-center gap-3 py-2.5'>
                                <span className='grid size-8 place-items-center rounded-full bg-white/[0.07] text-cream/80'>
                                    <item.icon aria-hidden='true' className='size-4' />
                                </span>

                                <span className='flex-1'>
                                    <span className='block text-sm text-cream'>
                                        {item.label}
                                    </span>

                                    <span className='block text-[11px] text-cream/45'>
                                        {item.detail}
                                    </span>
                                </span>

                                <span className={cn('text-sm tabular-nums', item.amount > 0 ? 'text-citron' : 'text-cream/80')}>
                                    {formatCurrency(item.amount, { decimals: 2, sign: true })}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='motion-float [animation-duration:6s]'>
                    <div className='flex items-center gap-4 rounded-full bg-cream py-1.5 pl-5 pr-1.5 text-black shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)]'>
                        <div className='leading-tight'>
                            <p className='text-[11px] text-black/55'>
                                Net today
                            </p>

                            <p className='text-lg font-medium tracking-[-0.03em] tabular-nums'>
                                {formatCurrency(net, { decimals: 2, sign: true })}
                            </p>
                        </div>

                        <button
                            type='button'
                            onClick={() => setOpen(value => !value)}
                            aria-expanded={open}
                            aria-controls='daily-transactions'
                            className='rounded-full bg-black px-4 py-2.5 text-xs font-medium text-cream transition-colors hover:bg-ink/80'
                        >
                            {open ? 'Hide activity' : 'See activity'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export const Analytics = () => {
    return (
        <section id='analytics' aria-label='Analytics' className='relative py-24 sm:py-32'>
            <div className='shell'>
                <SectionHeading
                    eyebrow='Clear numbers'
                    title='Cash flow you can *read in seconds*'
                    subtitle='Follow each dollar from the moment it lands to the moment it leaves, with balances that refresh as you spend.'
                />

                <div className='mt-16 grid gap-5 lg:grid-cols-[1.05fr_1fr] lg:gap-6'>
                    <Reveal className='h-full'>
                        <MonthlyOverviewCard />
                    </Reveal>

                    <Reveal delay={180} className='h-full'>
                        <DailySnapshotCard />
                    </Reveal>
                </div>
            </div>
        </section>
    )
}
